import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Translate from 'react-translate-component';
import { StickyTable, Row, Cell } from 'experium-react-sticky-table';
import { Spin } from 'antd';
import { isEmpty } from 'ramda';

import { DEFAULT_COLUMN_WIDTH } from 'constants/defaultColumns';
import { mapIndexed } from 'utils/ramdaAdditions';
import ErrorHTML from 'components/common/form/ErrorHTML';
import { spinDelay } from 'constants/defaults';
import withTableFitDimensions from './wrappers/withTableFitDimensions';

import TableContentCell from './TableContentCell';
import TableHeaderCell from './TableHeaderCell';

const withTableLoader = WrappedComponent => function TableLoader(props) {
    return (
        <Spin spinning={props.meta.pending} delay={spinDelay}>
            <div className={cx('react-table-container react-table-container-static', props.className)}>
                <div>
                    <WrappedComponent {...props} />
                </div>
            </div>
        </Spin>
    );
};

@withTableLoader
@withTableFitDimensions
export default class StaticTable extends Component {
    static propTypes = {
        meta: PropTypes.object,
        items: PropTypes.array,
        columns: PropTypes.array,
        lockedColumns: PropTypes.number,
        maxLines: PropTypes.string,
        height: PropTypes.number,
        refresh: PropTypes.func,
        handlePaginationVisible: PropTypes.func,
        footerColumns: PropTypes.array
    }

    renderTableHeader(columns) {
        return (
            <Row>
                {mapIndexed((column, colIndex) => {
                    if (!column) {
                        return null;
                    }

                    const widthStyle = {
                        width: column.width || 'auto'
                    };

                    return (
                        <Cell key={colIndex} style={widthStyle} className={column.additionalClass}>
                            <TableHeaderCell
                                column={column}
                                width={widthStyle}
                            />
                        </Cell>
                    );
                }, columns)}
            </Row>
        );
    }

    renderTableContent(columns, items) {
        return mapIndexed((item, rowIndex) => (
            <Row key={rowIndex} className={this.getRowClass(rowIndex + 1)}>
                { mapIndexed((column, key) => column ? (
                    <Cell key={key}>
                        <div style={{ width: column.width || DEFAULT_COLUMN_WIDTH }}
                             className={column.additionalClass}>
                            <TableContentCell
                                columnName={column.name}
                                rowIndex={rowIndex}
                                item={item}
                                value={item[column.name]}
                                template={column.template}
                            />
                        </div>
                    </Cell>
                ) : null, columns)}
            </Row>
        ), items);
    }

    render() {
        const { items = [], columns = [], refresh, meta, height, lockedColumns, maxLines = 'nolimit' } = this.props;

        const classes = cx('react-table', 'react-table-row-lines-' + maxLines);
        const style = {
            width: '100%',
            height: height + 'px'
        };

        return (
            <div ref={node => this.container = node} className={classes} style={style}>
                { isEmpty(items) && (
                    <div className="table-error">
                        { meta.error && <ErrorHTML meta={meta} refresh={refresh} /> }
                        { meta.success && <Translate content="common.no_data" /> }
                    </div>
                )}
                <StickyTable onScroll={this.handleScroll} stickyColumnsCount={lockedColumns}>
                    {this.renderTableHeader(columns)}
                    {this.renderTableContent(columns, items)}
                </StickyTable>
            </div>
        );
    }

    handleScroll = ({ scrollLeft, scrollTop = 0, scrollHeight = 0, clientHeight = 0 }) => {
        const isScrollEnd = scrollHeight - clientHeight - scrollTop < 20;
        const { handlePaginationVisible } = this.props;

        handlePaginationVisible && handlePaginationVisible(isScrollEnd);

        if (this.container) {
            this.container.classList.toggle('react-table-scrolled-y', scrollTop);
            this.container.classList.toggle('react-table-scrolled-x', scrollLeft);
        }
    }

    getRowClass = (rowIndex) => {
        const even = rowIndex && rowIndex % 2 === 0;
        const last = rowIndex === this.props.items.length;

        return cx(
            'table-row',
            even ? 'table-row-even' : 'table-row-odd',
            last ? 'table-row-last' : ''
        );
    }
}
